import { AuthLayout } from '@/components/auth'
import {
  Button,
  ButtonLink,
  Icon,
  Control,
  EmailInput,
  TextInput,
  useForm,
} from '@tempots/beatui'
import { attr, html, prop, OneOfValue, on, Ensure } from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { auth, authStore } from '@/services/gateway'
import { Validation } from '@tempots/std'
import z from 'zod'
import type { VerifyEmailResult } from '@/services/types'
import { isApiError } from '@/services/errors'

type VerifyState =
  | 'loading'
  | 'success'
  | 'success-signed-in'
  | 'error'
  | 'otp-entry'

// OTP verification schema using Zod
const otpSchema = z.object({
  email: z.email({ message: 'Please enter a valid email address' }),
  otp: z
    .string()
    .length(6, { message: 'Code must be 6 digits' })
    .regex(/^\d{6}$/, { message: 'Code must be 6 digits' }),
})

export function VerifyEmailPage() {
  const params = new URLSearchParams(window.location.search)
  const tokenValue = params.get('token') || ''
  const emailParam = params.get('email') || ''

  const state = prop<VerifyState>('loading')
  const errorMessage = prop<string>('')
  const resending = prop(false)

  // OTP form
  const { controller, submit, submitting } = useForm({
    initialValue: { email: emailParam, otp: '' },
    schema: otpSchema,
    onSubmit: async (value) => {
      try {
        const result = await auth.verifyEmail({
          otp: value.otp,
          email: value.email,
        })
        if (result.session_token) {
          await authStore.refresh()
          state.set('success-signed-in')
        } else {
          state.set('success')
        }
        return Validation.valid
      } catch (err) {
        const message = isApiError(err)
          ? (err.detail ?? err.title)
          : err instanceof Error
            ? err.message
            : 'Verification failed. Please try again.'
        return Validation.invalid({ message })
      }
    },
  })

  // Handle resend verification email
  const handleResend = async () => {
    const emailValue = controller.field('email').signal.get()
    if (!emailValue || resending.value) return

    resending.set(true)
    try {
      await auth.resendVerification({ email: emailValue })
      NavigationService.navigate(
        `/verify-email-sent?email=${encodeURIComponent(emailValue)}`
      )
    } catch (error) {
      const message = isApiError(error)
        ? (error.detail ?? error.title)
        : error instanceof Error
          ? error.message
          : 'Failed to resend email'
      errorMessage.set(message)
    } finally {
      resending.set(false)
    }
  }

  // Perform async verification on component initialization
  if (!tokenValue) {
    // No token, show OTP entry form
    state.set('otp-entry')
  } else {
    // Try to verify with token
    auth
      .verifyEmail({ token: tokenValue })
      .then(async (result: VerifyEmailResult) => {
        if (result.session_token) {
          await authStore.refresh()
          state.set('success-signed-in')
        } else {
          state.set('success')
        }
      })
      .catch((error: unknown) => {
        // Token verification failed, show OTP entry form
        state.set('otp-entry')
        errorMessage.set(
          error instanceof Error
            ? error.message
            : 'Verification link expired or invalid. Please enter the code from your email.'
        )
      })
  }

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full space-y-6'),
      OneOfValue(state, {
        loading: () =>
          html.div(
            attr.class('space-y-4 text-center'),
            html.div(
              attr.class(
                'w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto animate-pulse'
              ),
              Icon(
                { icon: 'mdi:email-outline' },
                attr.class('w-8 h-8 text-brand-600')
              )
            ),
            html.h2(
              attr.class('text-xl font-semibold text-gray-900'),
              'Verifying your email...'
            ),
            html.p(
              attr.class('text-gray-600'),
              'Please wait while we verify your email address.'
            )
          ),
        'success-signed-in': () =>
          html.div(
            attr.class('space-y-6 text-center'),
            html.div(
              attr.class(
                'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon({ icon: 'mdi:check' }, attr.class('w-8 h-8 text-green-600'))
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Email Verified!'
              ),
              html.p(
                attr.class('text-gray-600'),
                'Your email has been verified and you are now signed in.'
              )
            ),
            ButtonLink(
              { href: '/dashboard', fullWidth: true },
              attr.class('block'),
              html.span(
                attr.class('flex items-center justify-center gap-2'),
                'Continue to Dashboard',
                Icon({ icon: 'mdi:chevron-right' }, attr.class('w-4 h-4'))
              )
            )
          ),
        success: () =>
          html.div(
            attr.class('space-y-6 text-center'),
            html.div(
              attr.class(
                'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon({ icon: 'mdi:check' }, attr.class('w-8 h-8 text-green-600'))
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Email Verified!'
              ),
              html.p(
                attr.class('text-gray-600'),
                'Your email has been verified successfully. Please sign in to continue.'
              )
            ),
            ButtonLink(
              { href: '/signin', fullWidth: true },
              attr.class('block'),
              html.span(
                attr.class('flex items-center justify-center gap-2'),
                'Sign In',
                Icon({ icon: 'mdi:chevron-right' }, attr.class('w-4 h-4'))
              )
            )
          ),
        'otp-entry': () =>
          html.div(
            attr.class('space-y-6'),
            html.div(
              attr.class('text-center'),
              html.h2(
                attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
                'Verify your email'
              ),
              html.p(
                attr.class('mt-2 text-sm sm:text-base text-gray-600'),
                'Enter the 6-digit code from your email'
              )
            ),
            // Show initial error message if token failed
            errorMessage.map((msg) =>
              msg
                ? html.div(
                    attr.class(
                      'bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm'
                    ),
                    msg
                  )
                : null
            ),
            // Show form validation errors
            Ensure(controller.error, (error) =>
              html.div(
                attr.class(
                  'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
                ),
                error
              )
            ),
            html.form(
              on.submit((e) => {
                e.preventDefault()
                submit(e)
              }),
              attr.class('space-y-6'),
              html.div(
                Control(EmailInput, {
                  controller: controller.field('email'),
                  label: 'Email Address',
                  placeholder: 'you@company.com',
                })
              ),
              html.div(
                Control(TextInput, {
                  controller: controller.field('otp'),
                  label: 'Verification Code',
                  placeholder: '123456',
                })
              ),
              html.div(
                attr.class('flex items-center justify-center'),
                Button(
                  {
                    type: 'submit',
                    fullWidth: true,
                    loading: submitting,
                    disabled: submitting,
                  },
                  submitting.map((s) => (s ? 'Verifying...' : 'Verify Email'))
                )
              )
            ),
            html.div(
              attr.class('text-center space-y-4'),
              html.p(
                attr.class('text-sm text-gray-500'),
                "Didn't receive the email? ",
                html.button(
                  attr.type('button'),
                  on.click(handleResend),
                  attr.class(
                    resending.map((r: boolean) =>
                      r
                        ? 'font-medium text-gray-400 cursor-not-allowed'
                        : 'font-medium text-brand-600 hover:text-brand-700'
                    )
                  ),
                  resending.map((r: boolean) => (r ? 'Sending...' : 'Resend'))
                )
              ),
              html.div(
                Anchor(
                  { href: '/signin' },
                  attr.class(
                    'inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700'
                  ),
                  Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
                  'Back to sign in'
                )
              )
            )
          ),
        error: () =>
          html.div(
            attr.class('space-y-6 text-center'),
            html.div(
              attr.class(
                'w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon(
                { icon: 'mdi:alert-circle' },
                attr.class('w-8 h-8 text-red-600')
              )
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Verification Failed'
              ),
              html.p(
                attr.class('text-gray-600'),
                errorMessage.map(
                  (msg: string) =>
                    msg || 'The verification link is invalid or has expired.'
                )
              )
            ),
            Anchor(
              { href: '/signin' },
              attr.class('block'),
              Button(
                { fullWidth: true },
                html.span(
                  attr.class('flex items-center justify-center gap-2'),
                  Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
                  'Back to Sign In'
                )
              )
            )
          ),
      })
    )
  )
}
